class ModularProductPresentation extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.index = 0;
    this.abortController = new AbortController();
    this.fetchController = null;

    const { signal } = this.abortController;
    this.querySelector('[data-mpp-scroll-hint]')?.addEventListener('click', () => this.scrollToNextSection(), { signal });
    this.setupSlideshow();
    this.setupEmptyAccordionRows();

    this.addEventListener('change', (event) => this.scheduleVariantRefresh(event), { signal });

    ['variant:change', 'product:variant-change', 'shopify:variant-change'].forEach((eventName) => {
      document.addEventListener(eventName, (event) => this.scheduleVariantRefresh(event), { signal });
    });

    this.setupMobileStickyActions();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.slideshowAbortController?.abort();
    this.fetchController?.abort();
    this.mobileStickyResizeObserver?.disconnect();
    this.accordionContentObserver?.disconnect();
    if (this.accordionUpdateFrame != null) window.cancelAnimationFrame(this.accordionUpdateFrame);
    this.mobileStickyTransition?.cancel();
    this.mobileStickyPlaceholder?.remove();
    this.mobileStickyGroup?.classList.remove(
      'mpp__mobile-sticky-actions',
      'mpp__mobile-sticky-actions--docked'
    );
    this.mobileStickyGroup?.style.removeProperty('--mpp-docked-actions-gap');
    this.mobileStickyGroup?.style.removeProperty('--mpp-docked-background');
    this.initialized = false;
  }

  setupEmptyAccordionRows() {
    const detailsBlocks = this.querySelector('.mpp__details-blocks');
    if (!detailsBlocks) return;

    const hasContent = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return Boolean(node.nodeValue.replace(/[\s\u200B-\u200D\uFEFF]/g, ''));
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return false;
      if (node.hidden || node.getAttribute('aria-hidden') === 'true') return false;
      if (node.matches('script, style, template, noscript')) return false;
      if (node.matches('img[src], img[srcset], video, audio, iframe, canvas, svg, table, input:not([type="hidden"]), select, textarea')) return true;
      return [...node.childNodes].some(hasContent);
    };

    const refresh = () => {
      this.accordionUpdateFrame = null;
      for (const accordion of detailsBlocks.querySelectorAll('.accordion')) {
        const rows = [...accordion.querySelectorAll('accordion-custom')];
        if (!rows.length) continue;

        for (const row of rows) {
          const body = row.querySelector('details > .details-content');
          if (body) row.hidden = !hasContent(body);
          row.classList.remove('mpp__accordion-first-visible', 'mpp__accordion-last-visible');
        }

        const visibleRows = rows.filter((row) => !row.hidden);
        visibleRows[0]?.classList.add('mpp__accordion-first-visible');
        visibleRows[visibleRows.length - 1]?.classList.add('mpp__accordion-last-visible');
        const allEmpty = visibleRows.length === 0;
        accordion.hidden = allEmpty;
        const wrapper = accordion.closest('.shopify-block');
        if (wrapper && detailsBlocks.contains(wrapper) && wrapper.querySelectorAll('.accordion').length === 1) {
          wrapper.hidden = allEmpty;
        }
      }
    };

    refresh();
    if ('MutationObserver' in window) {
      this.accordionContentObserver = new MutationObserver(() => {
        if (this.accordionUpdateFrame != null) return;
        this.accordionUpdateFrame = window.requestAnimationFrame(refresh);
      });
      this.accordionContentObserver.observe(detailsBlocks, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }
  }

  setupMobileStickyActions() {
    const layout = this.querySelector('.mpp[data-mobile-sticky-actions]');
    const mode = layout?.dataset.mobileStickyActions;
    // Older section instances saved the previous checkbox as true or false.
    if (!layout || mode === 'none' || mode === 'false') return;
    const detailsBlocks = layout?.querySelector('.mpp__details-blocks');
    const anchor = detailsBlocks?.querySelector('.mpp-actions-anchor');
    if (!layout || !detailsBlocks || !anchor) return;

    let anchorBlock = anchor;
    while (anchorBlock.parentElement && anchorBlock.parentElement !== detailsBlocks) {
      anchorBlock = anchorBlock.parentElement;
    }

    if (anchorBlock.parentElement !== detailsBlocks) return;

    const group = anchorBlock.nextElementSibling;
    const groupSurface = group?.matches('.group-block')
      ? group
      : group?.querySelector(':scope > .group-block') || group?.querySelector('.group-block');
    if (!groupSurface) return;

    const groupContent = groupSurface.querySelector('.group-block-content');
    const selectedMode = mode === 'true' ? 'both' : mode;
    const selectedBlock = selectedMode === 'picker'
      ? groupContent?.querySelector('.variant-accordion')
      : selectedMode === 'buy'
        ? groupContent?.querySelector('.buy-buttons-block')
        : null;

    let stickyGroup = group;
    let stickySurface = groupSurface;
    const singleBlock = selectedMode === 'picker' || selectedMode === 'buy';
    if (singleBlock) {
      if (!selectedBlock) return;
      stickyGroup = selectedBlock;
      while (stickyGroup.parentElement && stickyGroup.parentElement !== groupContent) {
        stickyGroup = stickyGroup.parentElement;
      }
      if (stickyGroup.parentElement !== groupContent) return;
      stickySurface = stickyGroup;
    }

    anchorBlock.classList.add('mpp__actions-anchor-block');
    stickyGroup.classList.add('mpp__mobile-sticky-actions');
    layout.dataset.mppStickyReady = 'true';
    this.mobileStickyGroup = stickyGroup;
    this.mobileStickySurface = stickySurface;
    stickyGroup.addEventListener('change', (event) => this.scheduleVariantRefresh(event), {
      signal: this.abortController.signal,
    });

    const placeholder = document.createElement('div');
    placeholder.className = 'mpp__mobile-actions-placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    if (singleBlock) {
      placeholder.classList.add('mpp__mobile-actions-placeholder--nested');
      placeholder.style.display = 'none';
      stickyGroup.insertAdjacentElement('beforebegin', placeholder);
    } else {
      stickyGroup.insertAdjacentElement('afterend', placeholder);
    }
    this.mobileStickyPlaceholder = placeholder;

    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const transitionDuration = Number.parseFloat(
      window.getComputedStyle(layout).getPropertyValue('--mpp-actions-transition-duration')
    ) || 0;
    let updateFrame = null;
    let hasPositioned = false;

    const animateTransition = (firstRect) => {
      const lastRect = stickySurface.getBoundingClientRect();
      if (!hasPositioned || reducedMotionQuery.matches || transitionDuration <= 0) {
        hasPositioned = true;
        return;
      }

      const deltaX = firstRect.left - lastRect.left;
      const deltaY = firstRect.top - lastRect.top;
      const scaleX = lastRect.width > 0 ? firstRect.width / lastRect.width : 1;

      if (Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5 && Math.abs(scaleX - 1) < 0.002) return;

      this.mobileStickyTransition?.cancel();
      this.mobileStickyTransition = stickySurface.animate(
        [
          {
            transformOrigin: 'top left',
            transform: `translate3d(${deltaX}px, ${deltaY}px, 0) scaleX(${scaleX})`,
          },
          {
            transformOrigin: 'top left',
            transform: 'translate3d(0, 0, 0) scaleX(1)',
          },
        ],
        {
          duration: transitionDuration,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        }
      );
    };

    const restoreToNaturalPosition = () => {
      const wasDocked = stickyGroup.classList.contains('mpp__mobile-sticky-actions--docked');
      if (!wasDocked) {
        if (singleBlock) placeholder.style.display = 'none';
        else placeholder.style.height = '0px';
        hasPositioned = true;
        return;
      }

      const firstRect = stickySurface.getBoundingClientRect();
      stickyGroup.classList.remove('mpp__mobile-sticky-actions--docked');
      if (singleBlock) placeholder.style.display = 'none';
      else placeholder.style.height = '0px';
      animateTransition(firstRect);
    };

    const dockToViewport = (height) => {
      const layoutStyles = window.getComputedStyle(layout);
      const detailsStyles = window.getComputedStyle(detailsBlocks.closest('.mpp__details-column'));
      const alreadyDocked = stickyGroup.classList.contains('mpp__mobile-sticky-actions--docked');
      const firstRect = alreadyDocked ? null : stickySurface.getBoundingClientRect();

      placeholder.style.height = `${height}px`;
      if (singleBlock) placeholder.style.display = 'block';
      stickyGroup.style.setProperty(
        '--mpp-docked-actions-gap',
        layoutStyles.getPropertyValue('--mpp-actions-gap').trim() || '0px'
      );
      stickyGroup.style.setProperty(
        '--mpp-docked-background',
        detailsStyles.backgroundColor || '#fff'
      );

      if (alreadyDocked) return;

      stickyGroup.classList.add('mpp__mobile-sticky-actions--docked');
      animateTransition(firstRect);
    };

    const updateState = () => {
      updateFrame = null;
      if (!stickyGroup.isConnected || !placeholder.isConnected) return;

      if (!mobileQuery.matches) {
        restoreToNaturalPosition();
        layout.dataset.mppStickyState = 'desktop';
        layout.style.removeProperty('--mpp-mobile-sticky-height');
        return;
      }

      const height = Math.ceil(stickySurface.getBoundingClientRect().height);
      if (height <= 0) {
        layout.dataset.mppStickyState = 'unmeasurable';
        return;
      }
      const wasDocked = stickyGroup.classList.contains('mpp__mobile-sticky-actions--docked');
      if (wasDocked) this.mobileStickyDockedHeight = height;

      const dockingHeight = wasDocked ? this.mobileStickyDockedHeight || height : height;
      const naturalTop = singleBlock
        ? (wasDocked ? placeholder : stickyGroup).getBoundingClientRect().top
        : placeholder.getBoundingClientRect().top - (wasDocked ? 0 : height);
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const shouldDock = naturalTop > viewportHeight - dockingHeight;

      if (shouldDock) {
        dockToViewport(height);
        layout.dataset.mppStickyState = 'docked';
      } else {
        restoreToNaturalPosition();
        layout.dataset.mppStickyState = 'inline';
      }
      layout.style.setProperty('--mpp-mobile-sticky-height', `${height}px`);
    };

    const scheduleUpdate = () => {
      if (updateFrame !== null) return;
      updateFrame = window.requestAnimationFrame(updateState);
    };

    if ('ResizeObserver' in window) {
      this.mobileStickyResizeObserver = new ResizeObserver(scheduleUpdate);
      this.mobileStickyResizeObserver.observe(stickySurface);
    }

    window.addEventListener('scroll', scheduleUpdate, { signal: this.abortController.signal, passive: true });
    window.addEventListener('resize', scheduleUpdate, { signal: this.abortController.signal });
    mobileQuery.addEventListener('change', scheduleUpdate, { signal: this.abortController.signal });
    window.visualViewport?.addEventListener('resize', scheduleUpdate, { signal: this.abortController.signal });
    window.visualViewport?.addEventListener('scroll', scheduleUpdate, { signal: this.abortController.signal });
    scheduleUpdate();
  }

  setupSlideshow() {
    this.slideshowAbortController?.abort();
    this.slideshowAbortController = new AbortController();
    const { signal } = this.slideshowAbortController;

    this.index = 0;
    this.pointerStartX = null;
    this.track = this.querySelector('[data-mpp-track]');
    this.slides = [...this.querySelectorAll('[data-mpp-slide]')];
    this.dots = [...this.querySelectorAll('[data-mpp-dot]')];
    this.status = this.querySelector('[data-mpp-status]');

    this.querySelector('[data-mpp-previous]')?.addEventListener(
      'click',
      () => this.show(this.index - 1, true),
      { signal }
    );
    this.querySelector('[data-mpp-next]')?.addEventListener(
      'click',
      () => this.show(this.index + 1, true),
      { signal }
    );

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.show(Number(dot.dataset.mppDot), true), { signal });
    });

    const slideshow = this.querySelector('[data-mpp-slideshow]');

    slideshow?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.show(this.index - 1, true);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.show(this.index + 1, true);
      }
    }, { signal });

    slideshow?.addEventListener('pointerdown', (event) => {
      this.pointerStartX = event.clientX;
    }, { signal, passive: true });

    slideshow?.addEventListener('pointerup', (event) => {
      if (this.pointerStartX == null) return;
      const distance = event.clientX - this.pointerStartX;
      this.pointerStartX = null;
      if (Math.abs(distance) < 36) return;
      this.show(distance > 0 ? this.index - 1 : this.index + 1, true);
    }, { signal, passive: true });

    this.show(0, false);
  }

  show(nextIndex, announce = false) {
    if (!this.track || this.slides.length === 0) return;
    this.index = (nextIndex + this.slides.length) % this.slides.length;
    this.track.style.transform = `translateX(${-100 * this.index}%)`;

    this.slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', String(index !== this.index));
    });

    this.dots.forEach((dot, index) => {
      dot.setAttribute('aria-current', String(index === this.index));
    });

    if (announce && this.status) {
      this.status.textContent = `${this.index + 1} / ${this.slides.length}`;
    }
  }

  scrollToNextSection() {
    const sectionWrapper = this.closest('.shopify-section');
    const nextSection = sectionWrapper?.nextElementSibling;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    if (nextSection) {
      nextSection.scrollIntoView({ behavior, block: 'start' });
      return;
    }

    window.scrollBy({ top: Math.max(window.innerHeight * 0.75, this.offsetHeight * 0.5), behavior });
  }

  scheduleVariantRefresh(event) {
    window.clearTimeout(this.variantRefreshTimer);
    this.variantRefreshTimer = window.setTimeout(() => {
      const variantId = this.variantIdFromEvent(event) || this.currentVariantId();
      if (!variantId || String(variantId) === this.dataset.currentVariantId) return;
      this.refreshVariantPresentation(String(variantId));
    }, 100);
  }

  variantIdFromEvent(event) {
    return event?.detail?.variant?.id
      || event?.detail?.variantId
      || event?.detail?.id
      || event?.target?.dataset?.variantId
      || (event?.target?.name === 'id' ? event.target.value : null)
      || null;
  }

  currentVariantId() {
    const actionRoot = this.mobileStickyGroup;
    const checkedVariant = actionRoot?.querySelector('input[name="id"]:checked')
      || this.querySelector('input[name="id"]:checked');
    const formVariant = actionRoot?.querySelector('form [name="id"]')
      || this.querySelector('form [name="id"]');
    const dataVariant = actionRoot?.querySelector('[data-selected-variant-id]')
      || this.querySelector('[data-selected-variant-id]');
    const urlVariant = new URL(window.location.href).searchParams.get('variant');

    return checkedVariant?.value
      || formVariant?.value
      || dataVariant?.dataset?.selectedVariantId
      || urlVariant
      || null;
  }

  async refreshVariantPresentation(variantId) {
    const sectionId = this.dataset.sectionId;
    const productUrl = this.dataset.productUrl || window.location.pathname;
    if (!sectionId || !productUrl) return;

    this.fetchController?.abort();
    this.fetchController = new AbortController();

    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url, {
        signal: this.fetchController.signal,
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) return;

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const freshSection = documentFragment.querySelector(`[data-mpp-section][data-section-id="${CSS.escape(sectionId)}"]`);
      if (!freshSection || !this.isConnected) return;

      const currentSlideshow = this.querySelector('[data-mpp-slideshow]');
      const freshSlideshow = freshSection.querySelector('[data-mpp-slideshow]');
      const currentVariantContentBlocks = [...this.querySelectorAll('[data-mpp-variant-content][data-mpp-block-id]')];
      const currentLayout = this.querySelector('.mpp');
      const freshLayout = freshSection.querySelector('.mpp');

      if (currentSlideshow && freshSlideshow) {
        currentSlideshow.replaceWith(freshSlideshow);
        this.setupSlideshow();
      }
      currentVariantContentBlocks.forEach((currentBlock) => {
        const blockId = currentBlock.dataset.mppBlockId;
        const freshBlock = freshSection.querySelector(
          `[data-mpp-variant-content][data-mpp-block-id="${CSS.escape(blockId)}"]`
        );

        if (freshBlock) {
          currentBlock.innerHTML = freshBlock.innerHTML;
          currentBlock.hidden = freshBlock.hidden;
        }
      });
      if (currentLayout && freshLayout) {
        currentLayout.style.setProperty(
          '--mpp-natural-aspect-ratio',
          freshLayout.style.getPropertyValue('--mpp-natural-aspect-ratio')
        );
      }

      this.dataset.currentVariantId = variantId;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('Unable to refresh the product presentation for the selected variant.', error);
      }
    }
  }
}

if (!customElements.get('mpp-product-presentation-v6')) {
  customElements.define('mpp-product-presentation-v6', ModularProductPresentation);
}
